import * as React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { breakpoints } from '@styles/index';
import imageHelper from '@utils/imageHelper';
import Drawer from '@material-ui/core/Drawer';
import Container, {
  CardLogo,
  CardDescription,
  Feature,
  CardTitle,
  StyledButton,
  Flex
} from './styles';
import LearnMore from './LearnMore';

const FeaturesContainer = styled(Container)`
  @media (max-width: ${breakpoints.tablet}) {
    margin-top: 40px;
  }
  @media (max-width: ${breakpoints.mobile}) {
    margin-top: 32px;
    background: #ffffff;
    box-shadow: 0px 16px 32px rgba(221, 230, 237, 0.4);
    border-radius: 8px;
    padding: 16px;
  }
`;

const StyledFeature = styled(Feature)`
  margin-top: 0;
  @media (max-width: ${breakpoints.mobile}) {
    display: flex;
    align-items: ${({ $alignItems }) => $alignItems};
    margin-bottom: 12px;
    ${({ $last }) => $last && 'margin-bottom:0;'}
    margin-top: 0;
  }
`;

const LearnMoreContent = styled(Flex)`
  flex-direction: column;
  align-items: flex-start;
  margin-left: 16px;
  @media (max-width: ${breakpoints.mobile}) {
    display: block !important;
    align-items: baseline;
  }
`;

const Wrapper = styled(Flex)`
  display: none;
  @media (max-width: ${breakpoints.mobile}) {
    display: flex;
    margin-bottom: 12px;
  }
`;

const LearnMoreContainer = styled.div`
  display: none;
  @media (max-width: ${breakpoints.mobile}) {
    display: flex;
    align-items: center;
    margin-top: 24px;
  }
`;

const LearnMoreButton = styled.button`
  background: transparent;
  font-size: 14px;
  line-height: 14px;
  color: #08bd80;
  border: none;
  cursor: pointer;
  font-weight: 700;
  margin-right: 8px;
`;

const StyledCardTitle = styled(CardTitle)`
  @media (max-width: ${breakpoints.tablet}) {
    font-size: 18px;
    line-height: 120%;
  }
  @media (max-width: ${breakpoints.mobile}) {
    font-weight: 600;
    margin-left: 12px;
    font-size: 16px;
  }
`;

const LearnMoreCardTitle = styled(CardTitle)`
  @media (max-width: ${breakpoints.mobile}) {
    font-size: 18px;
    line-height: 120%;
    margin-left: 0 !important;
    margin-bottom: 4px;
  }
`;

const Button = styled(StyledButton)`
  margin-top: 16px;
  display: none;
  @media (max-width: ${breakpoints.mobile}) {
    display: block;
  }
`;

const Img = styled.img`
  object-fit: cover;
  cursor: pointer;
  user-drag: none;
  user-select: none;
  @media (max-width: ${breakpoints.tablet}) {
    width: ${({ $size }) => $size || 56}px;
    height: ${({ $size }) => $size || 56}px;
  }
  @media (max-width: ${breakpoints.mobile}) {
    width: ${({ $size }) => $size || 24}px;
    height: ${({ $size }) => $size || 24}px;
  }
`;

const FeatureDescription = styled(CardDescription)`
  @media (max-width: ${breakpoints.mobile}) {
    display: none;
  }
`;

const Benifits = ({ title, handleClick, benifits, benifitsTitle }) => {
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  return (
    <>
      <FeaturesContainer>
        <Wrapper>
          <CardDescription>
            {benifitsTitle || 'Best in class experience'}
          </CardDescription>
          <CardLogo onClick={handleOpen}>
            <img
              src={imageHelper.getImage('k12/expand.png')}
              alt="expand"
              width="24"
              height="24"
              loading="lazy"
            />
          </CardLogo>
        </Wrapper>
        {benifits.map((_feature, index) => (
          <StyledFeature
            key={_feature.id}
            $alignItems="center"
            $last={index === benifits.length - 1}
          >
            <CardLogo>
              <Img
                src={_feature.image}
                width="80"
                height="80"
                alt="test"
                loading="lazy"
              />
            </CardLogo>
            <CardLogo width="80" height="80" />
            <StyledCardTitle>{_feature.title}</StyledCardTitle>
            <FeatureDescription>{_feature.description}</FeatureDescription>
          </StyledFeature>
        ))}
        <LearnMoreContainer>
          <LearnMoreButton onClick={handleOpen}>Learn More</LearnMoreButton>
          <img
            src={imageHelper.getImage('k12/arrow-right.png')}
            alt="read more"
            width="14"
            height="14"
            loading="lazy"
          />
        </LearnMoreContainer>
      </FeaturesContainer>
      {title ? <Button onClick={handleClick}>{title}</Button> : null}
      <Drawer anchor="bottom" open={open} onClose={handleClose}>
        <LearnMore handleClose={handleClose}>
          {benifits.map((_feature) => (
            <StyledFeature key={_feature.id} $alignItems="flex-start">
              <CardLogo>
                <Img
                  src={_feature.image}
                  width="56"
                  height="56"
                  alt="test"
                  loading="lazy"
                  $size={56}
                />
              </CardLogo>
              <LearnMoreContent>
                <LearnMoreCardTitle>{_feature.title}</LearnMoreCardTitle>
                <CardDescription>{_feature.description}</CardDescription>
              </LearnMoreContent>
            </StyledFeature>
          ))}
        </LearnMore>
      </Drawer>
    </>
  );
};

Benifits.propTypes = {
  title: PropTypes.string,
  handleClick: PropTypes.func,
  benifits: PropTypes.arrayOf(PropTypes.any).isRequired,
  benifitsTitle: PropTypes.string
};

Benifits.defaultProps = {
  title: '',
  handleClick: () => {},
  benifitsTitle: 'Best in class experience'
};

export default Benifits;
